/// DOM wiring for the static landing page (`index.html`). Vanilla TS, no
/// React, no Firebase SDK — this page has to stay a fast, static-first
/// engagement surface, not a second copy of the game SPA. All the testable
/// decision logic lives in `landingCore.ts`; this file only touches
/// `document`/`fetch`/`localStorage`/`gtag`.

import { feedItems, pickCounters, type BoardFeedResponse } from './landingCore'
import type { TowerLogPayload } from '../data/towerLog'

/// Declared ambiently rather than imported — `gtag.js` is loaded as a plain
/// global `<script>` in `index.html`'s `<head>` (this page has no Firebase
/// Analytics SDK), so `gtag` may or may not exist depending on whether that
/// script has finished loading / was blocked. Every call site guards with
/// `typeof gtag !== 'undefined'` accordingly.
declare function gtag(...args: unknown[]): void

const FETCH_TIMEOUT_MS = 10_000

function onReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true })
  } else {
    fn()
  }
}

/// Swaps the free-play CTA's label for a returning player. Set by the game
/// SPA itself (`App.tsx` writes `fa_returning` on boot), so this only ever
/// flips on someone's *second* visit.
function initReturningPlayerCta(): void {
  const cta = document.getElementById('cta-play')
  if (!cta) return
  try {
    if (window.localStorage.getItem('fa_returning') === '1') {
      cta.textContent = 'Jump back in'
    }
  } catch {
    // localStorage unavailable (private browsing, disabled storage) — the
    // default "Play free in your browser" label is still correct.
  }
}

/// One delegated listener for every `[data-cta]` element on the page,
/// instead of a per-button handler. Never blocks navigation — this only
/// reports the tap, it doesn't intercept the click.
function initCtaTracking(): void {
  document.addEventListener('click', (evt) => {
    const target = evt.target
    if (!(target instanceof Element)) return
    const el = target.closest<HTMLElement>('[data-cta]')
    if (!el) return
    const cta = el.dataset.cta
    if (!cta) return
    if (typeof gtag !== 'undefined') {
      gtag('event', 'web_cta_tap', { target: cta })
    }
  })
}

function escapeHtml(value: string): string {
  const div = document.createElement('div')
  div.textContent = value
  return div.innerHTML
}

/// Fetches the same public `getTowerLog` endpoint the /towerlog page reads,
/// and reveals the strip ONLY on a usable response — any failure (network
/// error, non-2xx, not-yet-seeded payload, all 4 counters missing) leaves
/// the section `hidden`, matching the rest of the page's "never show a
/// broken widget" rule.
async function initTowerLogStrip(): Promise<void> {
  const section = document.getElementById('towerlog-strip')
  const grid = document.getElementById('towerlog-grid')
  if (!section || !grid) return
  try {
    const res = await fetch('/api/towerlog', { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
    if (!res.ok) return
    const payload = (await res.json()) as TowerLogPayload
    const counters = pickCounters(payload)
    if (counters.length === 0) return
    grid.innerHTML = counters
      .map(
        (c) => `
        <div class="tl-tile">
          <div class="tl-value">${escapeHtml(c.formatted)}</div>
          <div class="tl-label">${escapeHtml(c.label)}</div>
        </div>`,
      )
      .join('')
    section.hidden = false
  } catch {
    // Stay hidden — see the doc comment above.
  }
}

/// Fetches the public `getBoardFeed` endpoint and reveals the "Latest from
/// the tower" card only on a usable response; hidden on any failure.
async function initBoardFeed(): Promise<void> {
  const card = document.getElementById('board-feed-card')
  const list = document.getElementById('board-feed-list')
  if (!card || !list) return
  try {
    const res = await fetch('/api/board-feed', { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) })
    if (!res.ok) return
    const json = (await res.json()) as BoardFeedResponse
    const items = feedItems(json)
    if (items.length === 0) return
    list.innerHTML = items
      .map((item) => {
        const devTag = item.authorIsDeveloper ? '<span class="feed-tag">From the developer</span>' : ''
        const comments =
          item.commentCount > 0
            ? `<span class="feed-comments">${item.commentCount} repl${item.commentCount === 1 ? 'y' : 'ies'}</span>`
            : ''
        return `
        <li class="feed-item">
          <a class="feed-link" href="/talktothedeveloper?post=${encodeURIComponent(item.id)}">
            <span class="feed-title">${escapeHtml(item.title)}</span>
            <span class="feed-meta">${escapeHtml(item.dateLabel)}${devTag}${comments}</span>
            <span class="feed-snippet">${escapeHtml(item.snippet)}</span>
          </a>
        </li>`
      })
      .join('')
    card.hidden = false
  } catch {
    // Stay hidden — see the doc comment above.
  }
}

onReady(() => {
  initReturningPlayerCta()
  initCtaTracking()
  void initTowerLogStrip()
  void initBoardFeed()
})
